import type { ReactNode } from 'react'
import { twMerge } from 'tailwind-merge'

interface Props {
  children?: ReactNode
  interactive?: boolean
  className?: string
  footer?: ReactNode
}

export default function Tile({
  children,
  interactive = false,
  className,
  footer,
}: Props) {
  return (
    <div
      className={twMerge(
        'border-border-subtle relative flex flex-col justify-between gap-2 bg-white p-4',
        Boolean(interactive) && 'hover:bg-gray-20',
        className,
      )}
    >
      <div className="flex grow flex-col gap-2">{children}</div>
      <div
        className={twMerge(
          'flex items-end justify-end gap-2',
          Boolean(interactive || footer) && 'mt-11 min-h-4',
        )}
      >
        {footer && (
          <div className="text-text-secondary text-right text-xs">{footer}</div>
        )}
      </div>
    </div>
  )
}

interface TileHeaderProps {
  title: ReactNode
  description?: ReactNode
  actions?: ReactNode
  as?: 'h2' | 'h3'
  className?: string
}

export function TileHeader({
  title,
  description,
  actions,
  as: Heading = 'h2',
  className,
}: TileHeaderProps) {
  return (
    <div
      className={twMerge(
        'mb-4 flex w-full items-start justify-between gap-2',
        className,
      )}
    >
      <div>
        <Heading className="heading-02">{title}</Heading>
        {description && (
          <div className="body-01 text-text-secondary">{description}</div>
        )}
      </div>
      {actions}
    </div>
  )
}

export function TileSkeleton({
  className,
  interactive,
}: Pick<Props, 'interactive' | 'className'>) {
  return (
    <div
      className={twMerge(
        'flex flex-col justify-between gap-2 bg-white p-4',
        className,
      )}
    >
      <div className="flex grow animate-pulse flex-col gap-2">
        <div className="bg-gray-20 h-6 w-1/2" />
        <div className="bg-gray-20 mt-3 h-3 w-3/4" />
        <div className="bg-gray-20 h-3 w-full" />
        <div className="bg-gray-20 h-3 w-2/3" />
        <div className="bg-gray-20 h-3 w-1/2" />
      </div>
      {interactive && (
        <div className="mt-20 flex items-end justify-between gap-2">
          <div className="w-full">
            <div className="bg-gray-20 h-2 w-1/2" />
            <div className="bg-gray-20 mt-1 h-2 w-1/3" />
          </div>
        </div>
      )}
    </div>
  )
}
